import { Label, type LabelProps } from '@magicschool/spellbook/components/Label';
import type { ProgressBarProps } from '@magicschool/spellbook/components/ProgressBar/ProgressBar';
import { Slot } from '@magicschool/spellbook/components/Slot';
import { Spinner } from '@magicschool/spellbook/components/Spinner';
import { Text, type TextProps } from '@magicschool/spellbook/components/Text';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, useMemo } from 'react';

export type LabelSlotProps = {
  progressBarVariant: ProgressBarProps['variant'];
} & LabelProps;

const labelVariants = cva('h-6', {
  variants: {
    progressBarVariant: {
      default: '',
      inline: 'whitespace-nowrap',
    },
  },
});

export const LabelSlot = Slot<LabelSlotProps>({
  displayName: 'ProgressBar.Label',
  preventOverride: true,
  renderDefault: ({ className, progressBarVariant, ...props }) => (
    <Label
      data-spellbook-name="progress-bar-label"
      {...props}
      aria-hidden="true"
      className={cn(labelVariants({ progressBarVariant }), className)}
    />
  ),
});

export const HelperTextSlot = Slot<TextProps>({
  displayName: 'ProgressBar.HelperText',
  renderDefault: (props) => <Text data-spellbook-name="progress-bar-helper-text" color="default-soft" {...props} />,
});

const indicatorVariants = cva('flex gap-1 items-center justify-end', {
  variants: {
    variant: {
      default: 'grow',
      inline: '',
    },
  },
});

type IndicatorSlotProps = {
  hideSpinner?: boolean;
  ref?: React.RefObject<HTMLDivElement | null>;
  value: number;
} & VariantProps<typeof indicatorVariants>;

export const IndicatorSlot = Slot<IndicatorSlotProps>({
  displayName: 'ProgressBar.Indicator',
  renderDefault: (props) => {
    const Indicator: React.FC<IndicatorSlotProps> = useMemo(
      () =>
        forwardRef<HTMLDivElement, IndicatorSlotProps>(({ variant, value, hideSpinner, ...props }, forwardedRef) => (
          <div data-spellbook-name="progress-bar-indicator" {...props} aria-hidden="true" className={indicatorVariants({ variant })}>
            <Text ref={forwardedRef}>{`${value || 0}%`}</Text>
            {!hideSpinner && <Spinner size="md" />}
          </div>
        )),
      [],
    );

    return <Indicator {...props} />;
  },
});
